
import { RefObject, useState, useEffect, useRef } from 'react';
import { VideoFeatures } from '@/store/videoFeatures';
import { formatTime } from './utils/formatTime';
import { useVideoEvents } from './hooks/useVideoEvents';
import { useControlsVisibility } from './hooks/useControlsVisibility';
import { useVideoActions } from './hooks/useVideoActions';
import { useAutoplayOverlay } from './hooks/useAutoplayOverlay';
import { useContinueWatching } from './hooks/useContinueWatching';
import { useFinalCountdown } from './hooks/useFinalCountdown';

/**
 * Main hook for managing video player state
 */
export function useVideoPlayerState(
  videoRef: RefObject<HTMLVideoElement>,
  containerRef: RefObject<HTMLDivElement>,
  features: VideoFeatures,
  // true quando o player está montado dentro do editor/preview do dashboard.
  // Recursos que provocam navegação de verdade (hoje a Contagem Final) ficam
  // desarmados: o preview não pode arrastar o dashboard para fora.
  isEditorPreview = false
) {
  // Add states for thumbnails
  const [showInitialThumbnail, setShowInitialThumbnail] = useState(
    features.initialThumbnail?.enabled && !features.autoPlay
  );
  const [showPauseThumbnail, setShowPauseThumbnail] = useState(false);
  const [showEndThumbnail, setShowEndThumbnail] = useState(false);

  // Use composition of smaller hooks
  const {
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    isVertical,
    setIsMuted,
    setVolume,
    handleTimeUpdate,
    handleDurationChange,
    handlePlay,
    handlePause,
    handleVolumeChange,
    handleVideoEnded
  } = useVideoEvents(videoRef, features);

  const {
    autoplayOverlayEnabled,
    showAutoplayOverlay,
    handleAutoplayOverlayClick,
    showPlayIcon,
    handlePlayIconClick
  } = useAutoplayOverlay(videoRef, features, containerRef);

  const {
    showContinueWatchingOverlay,
    handleContinue,
    handleRestart,
    showSessionModal,
    sessionPosition,
    handleSessionResume,
    handleSessionRestart,
    handleSessionCancel,
    isPreviewMode
  } = useContinueWatching(videoRef, features);

  const {
    showOverlay: showFinalCountdown,
    timeRemaining: countdownTime,
    handleRedirect: handleCountdownRedirect,
    handleSkipCountdown,
    handleDismissCountdown,
    countdownCanGo,
    countdownHasRedirected
  } = useFinalCountdown(videoRef, features.finalCountdown, isEditorPreview);

  const {
    showControls,
    setShowControls
  } = useControlsVisibility(containerRef, isPlaying, features, showAutoplayOverlay);

  // Lógica para esconder controles antes de começar (removido - não implementado)
  // const [hasStarted, setHasStarted] = useState(false);
  const shouldShowControls = showControls;

  const videoActions = useVideoActions(videoRef, containerRef, isMuted, setIsMuted, setVolume);

  // Create wrapper functions that check disableControls
  const createDisabledWrapper = (action: (...args: any[]) => void) => {
    return (...args: any[]) => {
      if (!features.disableControls) {
        action(...args);
      }
    };
  };

  const {
    togglePlay: originalTogglePlay,
    handleSkipForward,
    handleSkipBack,
    toggleMute,
    handleVolumeSliderChange,
    handleSeek,
    toggleFullscreen,
    togglePictureInPicture,
    setPlaybackSpeed
  } = videoActions;

  // Wrap actions with disable check + bloqueio de pause. togglePlay é o
  // chokepoint de clique no vídeo / botão de play-pause / big play; com
  // blockPause ligado só barramos a ação de PAUSAR (mantém o play).
  const togglePlay = createDisabledWrapper(() => {
    if (features.blockPause && videoRef.current && !videoRef.current.paused) return;
    originalTogglePlay();
  });

  // Logic for initial thumbnail display based on dependencies
  useEffect(() => {
    const canShowInitialThumbnail = features.showControls && !features.autoPlay;

    if (!canShowInitialThumbnail && showInitialThumbnail) {
      setShowInitialThumbnail(false);
    } else if (canShowInitialThumbnail && features.initialThumbnail?.enabled && !isPlaying) {
      setShowInitialThumbnail(true);
    }
  }, [features.showControls, features.autoPlay, features.initialThumbnail?.enabled, isPlaying, showInitialThumbnail]);

  // Auto-play video when controls are disabled
  useEffect(() => {
    if (features.disableControls && videoRef.current) {
      // Add a small delay to avoid conflicts with other state updates
      const timer = setTimeout(async () => {
        const video = videoRef.current;
        if (video && video.paused) {
          try {
            await video.play();
          } catch (error) {
            // Silently handle autoplay errors (common in browsers with autoplay restrictions)
            if (error instanceof Error && error.name !== 'AbortError') {
              console.error('Error auto-playing video with disabled controls:', error);
            }
          }
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [features.disableControls]);

  // Enhanced play handler that also handles thumbnails
  const handleEnhancedPlay = () => {
    // Mark that video has started for hideControlsBeforeStart feature (removido - não implementado)
    // if (!hasStarted) {
    //   setHasStarted(true);
    // }

    setShowInitialThumbnail(false);
    setShowPauseThumbnail(false);
    
    if (showEndThumbnail) {
      // If we're at the end, restart the video
      const video = videoRef.current;
      if (video) {
        video.currentTime = 0;
        setShowEndThumbnail(false);
      }
    }
    
    handlePlay();
  };
  
  // Ref para controlar se o auto-pause já foi disparado (evita loop)
  const autoPauseFiredRef = useRef(false);

  // Converte "MM:SS" para segundos
  const parseTimeToSeconds = (t: string | number) => {
    if (!t) return 0;
    const parts = String(t).split(':');
    if (parts.length === 2) return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
    return Number(t) || 0;
  };

  // Auto-pause: quando o vídeo atingir o tempo definido, pausa e exibe a thumbnail
  useEffect(() => {
    if (
      features.pauseThumbnail?.enabled &&
      features.pauseThumbnail?.pauseMode === 'timeRange' &&
      features.pauseThumbnail?.timeRange?.start
    ) {
      const pauseAtSeconds = parseTimeToSeconds(features.pauseThumbnail.timeRange.start);

      if (currentTime < pauseAtSeconds) {
        // Reseta o ref se o usuário voltou antes do ponto de pause
        autoPauseFiredRef.current = false;
      } else if (isPlaying && !autoPauseFiredRef.current) {
        autoPauseFiredRef.current = true;
        setShowPauseThumbnail(true);
        videoRef.current?.pause();
      }
    }
  }, [currentTime, isPlaying, features.pauseThumbnail]);

  // Enhanced pause handler that handles thumbnails with advanced logic
  const handleEnhancedPause = () => {
    if (features.pauseThumbnail?.enabled) {
      const pauseMode = features.pauseThumbnail.pauseMode || 'always';

      if (pauseMode === 'always') {
        setShowPauseThumbnail(true);
      }
      // timeRange mode: thumbnail é controlada pelo useEffect de auto-pause acima
    }
    handlePause();
  };
  
  // Enhanced video ended handler
  const handleEnhancedVideoEnded = () => {
    if (features.endThumbnail?.enabled) {
      setShowEndThumbnail(true);
    }
    handleVideoEnded();
  };
  
  // Handle thumbnail click (typically play/resume)
  const handleThumbnailClick = () => {
    const video = videoRef.current;
    if (!video) return;
    
    if (showInitialThumbnail) {
      setShowInitialThumbnail(false);
      video.play().catch(err => console.error('Error playing video:', err));
    } else if (showPauseThumbnail) {
      setShowPauseThumbnail(false);
      video.play().catch(err => console.error('Error playing video:', err));
    } else if (showEndThumbnail) {
      // For end thumbnails, we might want different behavior
      // For now, restart the video
      video.currentTime = 0;
      setShowEndThumbnail(false);
      video.play().catch(err => console.error('Error playing video:', err));
    }
  };

  return {
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    isVertical,
    showAutoplayOverlay,
    showControls: shouldShowControls,
    showContinueWatchingOverlay,
    showInitialThumbnail,
    showPauseThumbnail,
    showEndThumbnail,
    handleContinue,
    handleRestart,
    togglePlay,
    handleTimeUpdate,
    handleDurationChange,
    handlePlay: handleEnhancedPlay,
    handlePause: handleEnhancedPause,
    handleVolumeChange,
    handleSkipForward,
    handleSkipBack,
    toggleMute,
    handleVolumeSliderChange,
    handleSeek,
    toggleFullscreen,
    togglePictureInPicture,
    setPlaybackSpeed,
    handleAutoplayOverlayClick,
    handleThumbnailClick,
    formatTime,
    setShowControls,
    autoplayOverlayEnabled,
    handleVideoEnded: handleEnhancedVideoEnded,
    showSessionModal,
    sessionPosition,
    handleSessionResume,
    handleSessionRestart,
    handleSessionCancel,
    showFinalCountdown,
    countdownTime,
    handleCountdownRedirect,
    handleSkipCountdown,
    handleDismissCountdown,
    countdownCanGo,
    countdownHasRedirected,
    isPreviewMode,
    showPlayIcon,
    handlePlayIconClick
  };
}
